// Help wanted (README "Help wanted"): real remote jobs from job boards that let other sites show their listings, on
// jobs/ (site/js/jobs/jobs-page.js). The live worker fetches the
// boards a few times a day and keeps one list for the whole site (worker/jobs.js), so the boards see a handful of
// requests a day, not one per visitor. Pure helpers only, so plain Node can test them (tests/jobs.test.mjs): the rules
// a job must pass, which the worker applies to what the boards send and the pages again to what the worker sends, and
// the rules of the "Post a job" form on jobs/, which the page checks before sending and the worker (worker/job-posts.js)
// enforces. A posted job shows once you approve it, on top, marked "Posted on Lofi Cities", and links to the company's
// own page for it.
import { cleanSay } from '../live/chat.js';

// The boards, and the credit their terms ask for: every job links to its page on the board that listed it (a followed
// link: Remote OK asks for no nofollow) and names that board. Never their logos (Remote OK's is a trademark).
export const BOARDS = {
  remoteok: { name: 'Remote OK', url: 'https://remoteok.com/', host: 'remoteok.com' },
  remotive: { name: 'Remotive', url: 'https://remotive.com/', host: 'remotive.com' },
  himalayas: { name: 'Himalayas', url: 'https://himalayas.app/', host: 'himalayas.app' },
  jobicy: { name: 'Jobicy', url: 'https://jobicy.com/', host: 'jobicy.com' },
};

// Jobs companies post here (the form on jobs/): once approved, the first in the list. Not a board: the
// credit lines leave it out.
export const POSTED = { id: 'lofi', name: 'Lofi Cities' };

const SITE = 'https://live.loficities.com/jobs';
const DEV = 'http://localhost:8787/jobs';           // pnpm live:dev
const DAY = 86400000;
export const JOB_LIFE_MS = 30 * DAY;                // a job posted longer ago than this is gone
export const JOBS_MAX = 40;                         // the list the worker serves
export const TICKER_MAX = 16;                       // the ones the strip across the top of the player runs through
export const TITLE_MAX = 80, COMPANY_MAX = 50, WHERE_MAX = 40, PAY_MAX = 30, TYPE_MAX = 20;
export const PLACE_MAX = 30, URL_MAX = 300, EMAIL_MAX = 254;   // the form's own fields
export const WORK = ['remote', 'hybrid', 'onsite'];
export const TYPES = ['Full-time', 'Part-time', 'Contract', 'Freelance', 'Internship', 'Temporary'];

/** The worker's job list for this page, or null: OBS pages, and local ones without ?live (like liveUrl in site/js/live/live.js). */
export function jobsUrl(loc, obs) {
  if (obs) return null;
  if (loc.hostname === 'loficities.com') return SITE;
  const local = /^(localhost|127\.0\.0\.1|\[::1\])$/.test(loc.hostname);
  return local && new URLSearchParams(loc.search).has('live') ? DEV : null;
}

/* ---------- the text rules ---------- */
const NAMED = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ', hellip: '…', ndash: '–', mdash: '—',
  lsquo: '‘', rsquo: '’', ldquo: '“', rdquo: '”', euro: '€', pound: '£' };
const entity = (all, name) => {
  const code = /^#x([0-9a-f]+)$/i.exec(name)?.[1] ?? /^#(\d+)$/.exec(name)?.[1];
  if (code == null) return NAMED[name.toLowerCase()] ?? all;
  const n = parseInt(code, /^#x/i.test(name) ? 16 : 10);
  return n > 0 && n <= 0x10FFFF && !(n >= 0xD800 && n <= 0xDFFF) ? String.fromCodePoint(n) : '';
};
// UTF-8 read as Latin-1 on the board's side ("MecÃ¡nico"): read back as UTF-8, when that's all it can be.
const MANGLED = /[\u00C2-\u00F4][\u0080-\u00BF]/;
function unmangle(s) {
  if (!MANGLED.test(s) || /[^\u0000-\u00FF]/.test(s)) return s;
  try { return new TextDecoder('utf-8', { fatal: true }).decode(Uint8Array.from(s, c => c.charCodeAt(0))); } catch { return s; }
}

/** A board's text as the pages show it: no tags or entities, one line, at most `max` characters (then "…"), or ''. */
export function cleanText(value, max) {
  if (typeof value !== 'string' && typeof value !== 'number') return '';
  const text = cleanSay(unmangle(String(value).replace(/<[^>]*>/g, ' ').replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, entity))) ?? '';
  const chars = [...text];
  return chars.length > max ? chars.slice(0, max - 1).join('').trimEnd() + '…' : text;
}

/** The job's page on its own board (https, that board's site), or null. */
export function boardUrl(value, board) {
  const host = BOARDS[board]?.host;
  if (!host || typeof value !== 'string') return null;
  try {
    const url = new URL(value);
    const theirs = url.hostname === host || url.hostname.endsWith('.' + host);
    return url.protocol === 'https:' && theirs && !url.username && !url.password ? url.href : null;
  } catch { return null; }
}

/** A posted job's link: the company's own page for it, over https, on a named host (not an address), or null. */
export function postUrl(value) {
  if (typeof value !== 'string' || !value.trim() || value.length > URL_MAX) return null;
  try {
    const url = new URL(value.trim());
    const named = /^[a-z0-9-]+(\.[a-z0-9-]+)*\.[a-z]{2,}$/.test(url.hostname) && !/^\d+(\.\d+){3}$/.test(url.hostname);
    return url.protocol === 'https:' && named && !url.username && !url.password ? url.href : null;
  } catch { return null; }
}

/** A job as the pages show it, or null: from its board (or posted here), still fresh, with a title, a company and its
 * link (on its board's own site, or for a posted job the company's). */
export function cleanJob(job, now) {
  const isPosted = job?.board === POSTED.id;
  if (!job || typeof job !== 'object' || !(isPosted || Object.hasOwn(BOARDS, job.board))) return null;
  const id = typeof job.id === 'string' && job.id.startsWith(job.board + ':') && /^[a-z]+:[\w-]{1,64}$/.test(job.id) ? job.id : null;
  const title = cleanText(job.title, TITLE_MAX), company = cleanText(job.company, COMPANY_MAX);
  const url = isPosted ? postUrl(job.url) : boardUrl(job.url, job.board);
  const posted = Number.isFinite(job.posted) ? Math.round(job.posted) : NaN;
  if (!id || !title || !company || !url || !(posted > now - JOB_LIFE_MS && posted < now + DAY)) return null;
  if (Number.isFinite(job.until) && job.until <= now) return null;   // the board says it has closed
  return {
    id, board: job.board, title, company,
    where: cleanText(job.where, WHERE_MAX), pay: cleanText(job.pay, PAY_MAX),
    type: TYPES.includes(job.type) ? job.type : '', cat: CATEGORY_IDS.has(job.cat) ? job.cat : categoryOf(title), url, posted,
  };
}

const first = (a, b) => (b.board === POSTED.id) - (a.board === POSTED.id) || b.posted - a.posted;   // posted jobs, then by age

/** The worker's answer as a list the pages can show: every job cleaned again, each once, the posted ones first, then
 * newest first. */
export function cleanJobs(data, now) {
  if (!Array.isArray(data?.jobs)) return [];
  const seen = new Set(), jobs = [];
  for (const raw of data.jobs.slice(0, JOBS_MAX * 2)) {
    const job = cleanJob(raw, now);
    if (job && !seen.has(job.id)) { seen.add(job.id); jobs.push(job); }
  }
  return jobs.sort(first).slice(0, JOBS_MAX);
}

/* ---------- what the pages show ---------- */
/** The strip's run (site/js/jobs/jobs-ticker.js): the posted jobs, then the newest of each board in turn, so every board gets its share. */
export function tickerJobs(jobs, max = TICKER_MAX) {
  const queues = Object.keys(BOARDS).map(board => jobs.filter(j => j.board === board));
  const run = jobs.filter(j => j.board === POSTED.id).slice(0, max);
  while (run.length < max && queues.some(q => q.length)) {
    for (const q of queues) if (q.length && run.length < max) run.push(q.shift());
  }
  return run;
}

/** One job on the strip: "Frontend Engineer · Acme · Worldwide · $90k–$120k". */
export const tickerText = job => [job.title, job.company, job.where, job.pay].filter(Boolean).join(' · ');

/** When it was posted, in words: "today", "yesterday", "3 days ago", "2 weeks ago". */
export function postedAgo(posted, now) {
  const days = Math.max(0, Math.floor((now - posted) / DAY));
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  return days < 14 ? `${days} days ago` : `${Math.floor(days / 7)} weeks ago`;
}

/* ---------- categories, badges and groups ---------- */
// What the job is, for the chips on jobs/: from its title first (the boards' own categories are sometimes wrong, and
// their words differ), then the board's category, then 'other'. The first rule that matches wins, so the order
// matters: a "Product Designer" is design, a "Sales Engineer" sales, a "Customer Success Engineer" support.
export const CATEGORIES = [
  { id: 'engineering', label: 'Engineering' },
  { id: 'design', label: 'Design' },
  { id: 'product', label: 'Product' },
  { id: 'data', label: 'Data & AI' },
  { id: 'marketing', label: 'Marketing' },
  { id: 'sales', label: 'Sales' },
  { id: 'support', label: 'Support' },
  { id: 'writing', label: 'Writing' },
  { id: 'teaching', label: 'Teaching' },
  { id: 'operations', label: 'Operations' },
  { id: 'other', label: 'Other' },
];
const CATEGORY_RULES = [
  ['support', /\b(customer|client) (service|support|success|care|experience)|\bsupport\b|help ?desk|service desk|call cent|kundenservice/i],
  ['design', /\bdesign(er|ers)?\b|\bui\b|\bux\b|graphic|illustrat|\bartist|animat|creative director/i],
  ['data', /\b(ai|ml|llm) (engineer|researcher|scientist|trainer|evaluator|analyst|specialist)|\bai\b.*\b(evaluator|trainer|analyst)\b|machine learning|data (scien|engineer|analyst|specialist)|\bdata\b.*\bannotat|annotat|\banalytics\b|algorithm|artificial intelligence/i],
  ['sales', /\bsales\b|business development|account (executive|manager)|\b(bdr|sdr)\b|partnership|territory/i],
  ['product', /\bproduct (manager|owner|lead|director|management|leader|operations)|project manag|program manag|\bscrum|delivery manager/i],
  ['marketing', /marketing|\bseo\b|growth|social media|\bsocial\b|\bbrand|community|communications?\b|\bcomms\b|campaign|public relations/i],
  ['writing', /\bwrit(er|ers|ing)\b|copywrit|\beditor\b|translat|journalis|proofread|content (writer|editor|strateg|creator)/i],
  ['teaching', /\bteach|tutor|\beducation\b|instructor|e-?learning|curriculum|\bcoach(ing)?\b/i],
  ['engineering', /engineer|developer|desarrollador|software|devops|\bsre\b|front-?end|back-?end|full[- ]?stack|\bmobile\b|\bios\b|android|programm|\bqa\b|\btest(er|ing)\b|security|sys ?admin|architect|technical staff|\bit\b|information technology|kubernetes|golang|python|\.net\b/i],
  ['operations', /operations|\bops\b|\bhr\b|human resources|\bpeople\b|talent|recruit|payroll|payment|financ|accounting|accountant|bookkeep|legal|paralegal|compliance|counsel|contracts|budget|admin|assistant|office|coordinator|pricing|supply chain|logistics|procurement|\bqms\b|quality|permitting/i],
];
const CATEGORY_IDS = new Set(CATEGORIES.map(c => c.id));

/** The job's category id (CATEGORIES): the first text, usually the title, then each next one, then 'other'. */
export function categoryOf(...texts) {
  for (const text of texts) {
    const words = String(text ?? '').replace(/[-_]+/g, ' ');   // Himalayas writes its categories as slugs
    for (const [id, rule] of CATEGORY_RULES) if (rule.test(words)) return id;
  }
  return 'other';
}

// The cities' accents (site/js/scenes/*.js), for the company badges: a company always gets the same one.
const BADGE_COLOURS = ['#f4a261', '#ff5fa2', '#ffd23f', '#e8413c', '#2ec4b6', '#e0a526', '#34e0a1', '#a38bff', '#ff6a3d',
  '#4fc3f7', '#b8e05a', '#c77dff', '#ff4d6d', '#f25fd0', '#7ddc82', '#5b8cff'];
const SUFFIXES = new Set(['inc', 'llc', 'ltd', 'gmbh', 'corp', 'co', 'the', 'sa', 'ag', 'bv']);

/** A company's badge: two letters ("Night Owl Studio" → "NO", "Bjak" → "BJ") and its colour. */
export function badgeOf(company) {
  const words = String(company).replace(/[^\p{L}\p{N}]+/gu, ' ').trim().split(' ').filter(w => w && !SUFFIXES.has(w.toLowerCase()));
  const chars = w => [...w];
  const letters = words.length > 1 ? chars(words[0])[0] + chars(words[1])[0] : chars(words[0] ?? '?').slice(0, 2).join('');
  let h = 2166136261;                                  // FNV-1a over the name, so the colour never changes
  for (const c of String(company).toLowerCase()) h = Math.imul(h ^ c.codePointAt(0), 16777619) >>> 0;
  return { letters: letters.toUpperCase() || '?', colour: BADGE_COLOURS[h % BADGE_COLOURS.length] };
}

// The list's groups on jobs/, in order: the jobs posted here, then the boards' by how new they are.
export const GROUPS = [
  { id: 'posted', label: `Posted on ${POSTED.name}` },
  { id: 'today', label: 'Today' },
  { id: 'week', label: 'This week' },
  { id: 'earlier', label: 'Earlier' },
];
/** Which group a job goes in. */
export function groupOf(job, now) {
  if (job.board === POSTED.id) return 'posted';
  const age = now - job.posted;
  return age < DAY ? 'today' : age < 7 * DAY ? 'week' : 'earlier';
}

/** Its age, short, for the right edge of its row: "today", "3d", "2w". */
export function ageShort(posted, now) {
  const days = Math.max(0, Math.floor((now - posted) / DAY));
  return days === 0 ? 'today' : days < 14 ? `${days}d` : `${Math.floor(days / 7)}w`;
}

/* ---------- search and filters on jobs/ ---------- */
// The chips under the search box, besides one for each type (TYPES) the list has. A chip shows only when it would
// narrow the list: it matches some of the jobs, not all.
export const CHIPS = [
  { id: 'remote', label: 'Remote' },
  { id: 'posted', label: `Posted on ${POSTED.name}` },
  { id: 'pay', label: 'With salary' },
];
const fold = s => String(s).normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();   // "Café" finds "cafe"
// every job from the boards is remote (they're remote job boards); a posted one says so in where
const isRemote = job => job.board !== POSTED.id || /^remote\b/i.test(job.where);
const TESTS = { remote: isRemote, posted: job => job.board === POSTED.id, pay: job => Boolean(job.pay) };

/** The filters as the address has them: ?q=designer&cat=design&remote=1&posted=1&pay=1&type=Contract&type=Freelance. */
export function filtersFrom(search) {
  const p = new URLSearchParams(search);
  return {
    q: (p.get('q') ?? '').slice(0, 60), cat: CATEGORY_IDS.has(p.get('cat')) ? p.get('cat') : '',
    ...Object.fromEntries(CHIPS.map(c => [c.id, p.get(c.id) === '1'])),
    types: [...new Set(p.getAll('type'))].filter(t => TYPES.includes(t)),
  };
}

/** The address's query with these filters in it, and anything else it had (?live) left alone. */
export function filtersInto(search, filters) {
  const p = new URLSearchParams(search);
  for (const k of ['q', 'cat', 'type', ...CHIPS.map(c => c.id)]) p.delete(k);
  if (filters.q.trim()) p.set('q', filters.q.trim());
  if (filters.cat) p.set('cat', filters.cat);
  for (const c of CHIPS) if (filters[c.id]) p.set(c.id, '1');
  for (const t of filters.types) p.append('type', t);
  const s = p.toString().replace(/=(?=&|$)/g, '');   // ?live stays ?live, not ?live=
  return s ? `?${s}` : '';
}

/** Whether a job passes: every word of the search in its title, company, place or type (accents and case aside),
 * the picked category, every chip that's on, and one of the picked types if any are. */
export function jobMatches(job, { q = '', cat = '', types = [], ...chips } = {}) {
  const haystack = fold([job.title, job.company, job.where, job.type].join(' '));
  if (!fold(q).split(/\s+/).filter(Boolean).every(word => haystack.includes(word))) return false;
  if (cat && job.cat !== cat) return false;
  if (CHIPS.some(c => chips[c.id] && !TESTS[c.id](job))) return false;
  return !types.length || types.includes(job.type);
}

/** The chips worth showing for this list, each with how many jobs it matches: [{ id, label, type?, count }]. */
export function chipsFor(jobs) {
  const all = [...CHIPS.map(c => ({ ...c, test: TESTS[c.id] })), ...TYPES.map(t => ({ id: `type:${t}`, label: t, type: t, test: j => j.type === t }))];
  return all.map(({ test, ...c }) => ({ ...c, count: jobs.filter(test).length }))
    .filter(c => c.count > 0 && c.count < jobs.length);
}

/** The category chips for this list, most jobs first, each with its count: none unless it has two categories or more. */
export function categoriesFor(jobs) {
  const counts = CATEGORIES.map(c => ({ ...c, count: jobs.filter(j => j.cat === c.id).length })).filter(c => c.count);
  return counts.length > 1 ? counts.sort((a, b) => (a.id === 'other') - (b.id === 'other') || b.count - a.count) : [];   // Other last
}

/** "31 jobs", "1 job", "5 of 31 jobs". */
export const jobsCount = (shown, total) => `${shown === total ? total : `${shown} of ${total}`} ${total === 1 ? 'job' : 'jobs'}`;

/* ---------- the "Post a job" form ---------- */
const EMAIL = /^[^\s@]{1,64}@[a-z0-9-]+(\.[a-z0-9-]+)*\.[a-z]{2,}$/i;
const within = (text, max) => [...text].length <= max;

/**
 * The form's fields as a job to review: { post } with the text cleaned and where it is ("Remote", "Remote · Europe",
 * "Berlin (hybrid)", "Berlin"), or { why } naming the first field that isn't right. Too long is wrong, not cut.
 */
export function cleanPost(input) {
  if (!input || typeof input !== 'object') return { why: 'title' };
  const text = (k, max) => { const t = cleanText(input[k], Infinity); return within(t, max) ? t : null; };
  const title = text('title', TITLE_MAX), company = text('company', COMPANY_MAX), url = postUrl(input.url);
  const work = WORK.includes(input.work) ? input.work : null, place = text('place', PLACE_MAX), pay = text('pay', PAY_MAX);
  const type = TYPES.includes(input.type) ? input.type : null;
  const email = typeof input.email === 'string' ? input.email.trim() : '';
  if (!title || [...title].length < 3) return { why: 'title' };
  if (!company || [...company].length < 2) return { why: 'company' };
  if (!url) return { why: 'url' };
  if (!work) return { why: 'work' };
  if (place == null || (work !== 'remote' && !place)) return { why: 'place' };
  if (!type) return { why: 'type' };
  if (pay == null) return { why: 'pay' };
  if (email.length > EMAIL_MAX || !EMAIL.test(email)) return { why: 'email' };
  const where = work === 'remote' ? (place ? `Remote · ${place}` : 'Remote') : work === 'hybrid' ? `${place} (hybrid)` : place;
  return { post: { title, company, url, work, place, where, type, pay, email } };
}

/** The worker's list, cleaned; [] when it can't be had (offline, a worker from before the jobs, a slow answer). */
export async function loadJobs(url, { now = Date.now, timeout = 8000 } = {}) {
  try {
    const res = await fetch(url, { credentials: 'omit', cache: 'no-store', signal: AbortSignal.timeout(timeout) });
    return res.ok ? cleanJobs(await res.json(), now()).filter(job => job.board === POSTED.id) : [];
  } catch { return []; }
}
